/**
 * Login component
 */
import { Component, OnInit, OnDestroy } from '@angular/core';
import { NgRedux, select } from '@angular-redux/store';
import { rx, Observable, Subscription } from '@shared/rx';
import { IAppState } from '@store/reducers';
import { Router } from '@angular/router';
import { SessionActions } from '@store/actions';
import { IUserSession } from '@store/reducers/session';
import { ILoginClient, ICredentials } from '@shared/types';
import { environment } from '../../../../environments/environment';
import { DASHBOARD_ROUTE } from '@app/routes';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xvi-login',
  styleUrls: ['./login.scss'],
  templateUrl: './login.template.html',
})
export class LoginComponent implements OnInit, OnDestroy {
  public userSession: Observable<IUserSession> =
    this.ngRedux.select('userSession');
  public client: ILoginClient = {
    email: '',
    password: '',
  };
  public errorMessage: string = '';
  public isProduction: boolean = environment.production;
  @select(['userSession']) private session$: Observable<IUserSession>;
  private sessionSubscription: Subscription;

  /**
   * Initialize the component
   * Components need initialization in the constructor.
   */
  public constructor(
    private sessionActions: SessionActions,
    private ngRedux: NgRedux<IAppState>,
    private router: Router,
  ) {}

  /**
   * This function is called right after the directive's
   * data-bound properties have been checked for the first time,
   * and before any of its children have been checked.
   * It is invoked only once when the directive is instantiated.
   * In this function we subscribe to user session state changes.
   * And once user session is changed we check if it contains errors
   * and show them
   */
  public ngOnInit(): void {
    this.sessionSubscription = this.session$
      .pipe(
        rx.filter((session) => {
          const { user, errors } = session || { user: undefined, errors: [] };

          const errorMessage: string =
            errors && errors[0] && (errors[0].message || errors[0].title);
          if (errorMessage) {
            this.errorMessage = errorMessage;
          }

          return user && !!user.email;
        }),
      )
      .subscribe(() => {
        this.router.navigate([DASHBOARD_ROUTE]);
      });
  }

  public ngOnDestroy(): void {
    if (this.sessionSubscription) this.sessionSubscription.unsubscribe();
  }

  /**
   * Perform a login operation
   */
  public onSubmit(): void {
    this.errorMessage = '';
    const credentials: ICredentials = {
      user: this.client.email,
      password: this.client.password,
    };
    this.sessionActions.fetchPersonalToken(credentials);
  }
}
